import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { g, p, del, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ShieldAlert, ShieldCheck, Check, X, AlertTriangle } from "lucide-react";

const quando = (s) =>
  s ? new Date(s).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "";

const ETICHETTE = {
  approved: ["Autorizzato da te", "text-emerald-700 bg-emerald-50"],
  emergency: ["Accesso d'emergenza", "text-rose-700 bg-rose-50"],
  denied: ["Rifiutato da te", "text-slate-600 bg-slate-100"],
  expired: ["Richiesta decaduta", "text-slate-600 bg-slate-100"],
  closed: ["Accesso chiuso", "text-slate-600 bg-slate-100"],
};

const PORTA = [
  { h: 1, l: "1 ora" },
  { h: 8, l: "8 ore" },
  { h: 24, l: "1 giorno" },
];

/** Registro degli accessi dell'assistenza: il titolare autorizza e vede tutto lo storico. */
export const AccessiFornitore = () => {
  const { user } = useApp();
  const [d, setD] = useState(null);

  const load = useCallback(() => {
    g("/support/access").then(setD).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const i = setInterval(load, 30000);
    return () => clearInterval(i);
  }, [load]);

  if (!d) return null;

  const apri = async (h) => {
    try {
      await p("/support/gate", { hours: h, by: user?.name || "Titolare" });
      toast.success("Accesso aperto");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const chiudi = async () => {
    try {
      await del(`/support/gate?by=${encodeURIComponent(user?.name || "Titolare")}`);
      toast.success("Accesso chiuso: l'assistenza dovrà chiedertelo di nuovo");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const decidi = async (id, cosa) => {
    try {
      await p(`/support/access/${id}/${cosa}`, { by: user?.name || "Titolare" });
      toast.success(cosa === "approve" ? "Accesso autorizzato per un'ora" : "Accesso rifiutato");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <Card className="space-y-4 p-6" data-testid="accessi-fornitore-card">
      <div className="flex items-start gap-3">
        <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
        <div>
          <h3 className="font-semibold">Chi è entrato nel tuo gestionale</h3>
          <p className="mt-1 text-sm text-slate-500">
            L'assistenza non entra quando vuole: deve chiedertelo, tu autorizzi e l'accesso si chiude da
            solo dopo {d.durata_minuti} minuti. Qui resta scritto tutto, e queste righe non si possono
            cancellare.
          </p>
        </div>
      </div>

      <div className={`rounded-xl border p-4 ${d.gate?.open ? "border-emerald-300 bg-emerald-50" : "border-slate-200"}`}
           data-testid="gate-assistenza">
        <p className="text-sm font-semibold">
          {d.gate?.open ? "Accesso all'assistenza: aperto" : "Accesso all'assistenza: chiuso"}
        </p>
        <p className="mt-1 text-xs text-slate-500">
          {d.gate?.open
            ? `L'assistenza può entrare in sola lettura fino al ${quando(d.gate.until)}.`
            : "Se preferisci, lascia la porta aperta per un po': l'assistenza entrerà in sola lettura senza doverti chiedere ogni volta. Si richiude da sola entro 24 ore."}
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          {d.gate?.open ? (
            <Button size="sm" variant="outline" className="border-rose-300 text-rose-700"
                    data-testid="gate-chiudi" onClick={chiudi}>
              <X className="mr-1 h-4 w-4" /> Chiudi l'accesso
            </Button>
          ) : (
            PORTA.map((o) => (
              <Button key={o.h} size="sm" variant="outline" data-testid={`gate-apri-${o.h}`} onClick={() => apri(o.h)}>
                {o.l}
              </Button>
            ))
          )}
        </div>
      </div>

      {d.pending.map((r) => (
        <div key={r.id} className="rounded-xl border border-amber-300 bg-amber-50 p-4" data-testid={`accesso-pending-${r.id}`}>
          <p className="flex items-center gap-2 text-sm font-bold text-amber-800">
            <ShieldAlert className="h-4 w-4" /> L'assistenza chiede di entrare
          </p>
          <p className="mt-1.5 text-sm text-slate-700">Motivo: <b>{r.reason}</b></p>
          <p className="text-xs text-slate-500">Richiesto il {quando(r.at)}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" data-testid={`accesso-approva-${r.id}`} onClick={() => decidi(r.id, "approve")}>
              <Check className="mr-1 h-4 w-4" /> Autorizza per {r.durata_minuti >= 60 ? `${Math.round(r.durata_minuti / 60)} ore` : `${r.durata_minuti} minuti`}
            </Button>
            <Button size="sm" variant="outline" className="border-rose-300 text-rose-700"
                    data-testid={`accesso-rifiuta-${r.id}`} onClick={() => decidi(r.id, "deny")}>
              <X className="mr-1 h-4 w-4" /> Rifiuta
            </Button>
          </div>
        </div>
      ))}

      {d.storico.length === 0 ? (
        <p className="rounded-xl bg-emerald-50 p-4 text-sm text-emerald-800" data-testid="accessi-vuoto">
          <b>Nessun accesso negli ultimi {d.giorni} giorni.</b> Nessuno dell'assistenza è mai entrato nel
          tuo locale.
        </p>
      ) : (
        <div className="space-y-2" data-testid="accessi-storico">
          {d.storico.map((r) => {
            const [testo, stile] = ETICHETTE[r.status] || ["Accesso", "text-slate-600 bg-slate-100"];
            return (
              <div key={r.id} className="rounded-lg border border-slate-200 p-3" data-testid={`accesso-riga-${r.id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${stile}`}>
                    {r.emergency && <AlertTriangle className="mr-1 inline h-3 w-3" />}
                    {testo}
                  </span>
                  <span className="text-xs text-slate-500">{quando(r.at)}</span>
                </div>
                <p className="mt-1 text-sm text-slate-700">{r.reason}</p>
                <p className="text-xs text-slate-500">
                  {r.entered_at ? `Entrato il ${quando(r.entered_at)}` : "Non è entrato"}
                  {r.approved_by && r.status !== "emergency" ? ` · deciso da ${r.approved_by}` : ""}
                </p>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
};
